import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useSelector } from 'react-redux';
import {
  dismissWalkthroughPermanently,
  isWalkthroughDismissed,
  isWalkthroughSkippedForSession,
  requestOpenAddCoinModal,
  skipWalkthroughForSession,
} from 'utils/onboardingWalkthrough';

const STEPS = [
  {
    id: 'intro',
    target: null,
    title: 'Welcome to XGain',
    body: 'This quick tour shows you how to add coins to your portfolio. It only takes a minute.',
    placement: 'center',
  },
  {
    id: 'add-button',
    target: '#addCoinBtn',
    title: 'Tap Add',
    body: 'Use Add to search for a coin and record the quantity and price you paid.',
    placement: 'top',
  },
  {
    id: 'coin-search',
    target: '#addCoinSearch',
    title: 'Search for a coin',
    body: 'Type at least 3 letters (e.g. BTC, ETH) and pick a coin from the list.',
    placement: 'bottom',
    openAddModal: true,
  },
  {
    id: 'quantity',
    target: '#addCoinQty',
    title: 'Enter quantity',
    body: 'How many coins did you buy?',
    placement: 'bottom',
    requiresAddModal: true,
  },
  {
    id: 'avg-buy',
    target: '#addCoinAvgBuy',
    title: 'Enter your buy price',
    body: 'Average Buy is the price you paid per coin in USD.',
    placement: 'bottom',
    requiresAddModal: true,
  },
  {
    id: 'category',
    target: '#addCoinCategory',
    title: 'Pick a category',
    body: 'Tap a pill to sort your coin — BlueChip, DeFi, Gaming, Metaverse, and more.',
    placement: 'bottom',
    requiresAddModal: true,
    proOnly: true,
  },
  {
    id: 'save',
    target: '#addCoinSave',
    title: 'Save your coin',
    body: 'Hit Save to add it to your portfolio. Charts and your XGain multiplier update right away.',
    placement: 'top',
    requiresAddModal: true,
  },
];

const buildSteps = (isPro) => STEPS.filter((step) => !step.proOnly || isPro);

const hasBottomTabBar = (pathname) => {
  if (pathname === '/dashboard/assets' || pathname === '/dashboard/all-coin') return true;
  if (pathname === '/dashboard/main' || pathname.startsWith('/dashboard/main/')) return true;
  return false;
};

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_GAP = 14;
const POLL_MS = 120;
const MAX_POLL_ATTEMPTS = 40;

const waitForElement = (selector, attempts = MAX_POLL_ATTEMPTS) => new Promise((resolve) => {
  let count = 0;
  const tick = () => {
    const el = document.querySelector(selector);
    if (el) {
      resolve(el);
      return;
    }
    count += 1;
    if (count >= attempts) {
      resolve(null);
      return;
    }
    setTimeout(tick, POLL_MS);
  };
  tick();
});

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function getTooltipPosition(placement, rect, tooltipSize) {
  const margin = 16;
  const { width: tw, height: th } = tooltipSize;

  if (placement === 'center' || !rect) {
    return {
      top: clamp(window.innerHeight / 2 - th / 2, margin, window.innerHeight - th - margin),
      left: clamp(window.innerWidth / 2 - tw / 2, margin, window.innerWidth - tw - margin),
    };
  }

  let top = rect.top - th - TOOLTIP_GAP;
  let left = rect.left + rect.width / 2 - tw / 2;

  if (placement === 'bottom') {
    top = rect.bottom + TOOLTIP_GAP;
  }

  if (placement === 'top' && top < margin) {
    top = rect.bottom + TOOLTIP_GAP;
  }

  if (placement === 'bottom' && top + th > window.innerHeight - margin) {
    top = rect.top - th - TOOLTIP_GAP;
  }

  return {
    top: clamp(top, margin, window.innerHeight - th - margin),
    left: clamp(left, margin, window.innerWidth - tw - margin),
  };
};

export default function OnboardingWalkthrough() {
  const { user, plan, loaded } = useSelector((state) => state.user);
  const location = useLocation();
  const navigate = useNavigate();
  const userId = user?._id;

  const [active, setActive] = React.useState(false);
  const [stepIndex, setStepIndex] = React.useState(0);
  const [doNotShowAgain, setDoNotShowAgain] = React.useState(false);
  const [targetRect, setTargetRect] = React.useState(null);
  const [waitingForTarget, setWaitingForTarget] = React.useState(false);

  const startedRef = React.useRef(false);
  const welcomeOpenRef = React.useRef(false);

  const isPro = plan?.name === 'Pro';
  const steps = React.useMemo(() => buildSteps(isPro), [isPro]);

  const tooltipRef = React.useRef(null);
  const step = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;

  const finish = React.useCallback((permanent) => {
    if (permanent && userId) {
      dismissWalkthroughPermanently(userId);
    } else if (userId) {
      skipWalkthroughForSession(userId);
    }
    setActive(false);
  }, [userId]);

  const updateTargetRect = React.useCallback(() => {
    if (!step?.target) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (!el) {
      setTargetRect(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setTargetRect({
      top: rect.top - SPOTLIGHT_PADDING,
      left: rect.left - SPOTLIGHT_PADDING,
      width: rect.width + SPOTLIGHT_PADDING * 2,
      height: rect.height + SPOTLIGHT_PADDING * 2,
    });
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [step]);

  React.useEffect(() => {
    if (!loaded || !userId || !plan?.name) return undefined;
    if (isWalkthroughDismissed(userId) || isWalkthroughSkippedForSession(userId)) return undefined;

    const startTour = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      setActive(true);
    };

    const onWelcomeOpened = () => {
      welcomeOpenRef.current = true;
    };

    const onWelcomeClosed = () => {
      welcomeOpenRef.current = false;
      setTimeout(startTour, 350);
    };

    window.addEventListener('xgain:welcome-opened', onWelcomeOpened);
    window.addEventListener('xgain:welcome-closed', onWelcomeClosed);

    const fallback = setTimeout(() => {
      if (!welcomeOpenRef.current) {
        startTour();
      }
    }, 1200);

    return () => {
      window.removeEventListener('xgain:welcome-opened', onWelcomeOpened);
      window.removeEventListener('xgain:welcome-closed', onWelcomeClosed);
      clearTimeout(fallback);
    };
  }, [loaded, userId, plan?.name]);

  React.useEffect(() => {
    if (!active) return;
    if (!hasBottomTabBar(location.pathname)) {
      navigate('/dashboard/main');
    }
  }, [active, location.pathname, navigate]);

  React.useEffect(() => {
    if (!active || !step) return undefined;

    let cancelled = false;

    const prepareStep = async () => {
      setWaitingForTarget(true);
      setTargetRect(null);

      if (step.openAddModal) {
        requestOpenAddCoinModal();
      }

      if (step.target) {
        const el = await waitForElement(step.target);
        if (cancelled) return;
        if (!el && step.requiresAddModal) {
          requestOpenAddCoinModal();
          await waitForElement(step.target);
        }
      }

      if (cancelled) return;
      setWaitingForTarget(false);
      updateTargetRect();
    };

    prepareStep();

    return () => {
      cancelled = true;
    };
  }, [active, step, stepIndex, updateTargetRect]);

  React.useEffect(() => {
    if (!active) return undefined;

    const onLayoutChange = () => updateTargetRect();
    window.addEventListener('resize', onLayoutChange);
    window.addEventListener('scroll', onLayoutChange, true);

    const observer = new MutationObserver(onLayoutChange);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      window.removeEventListener('resize', onLayoutChange);
      window.removeEventListener('scroll', onLayoutChange, true);
      observer.disconnect();
    };
  }, [active, stepIndex, updateTargetRect]);

  const handleNext = () => {
    if (isLastStep) {
      finish(doNotShowAgain);
      return;
    }
    setStepIndex((current) => current + 1);
  };

  const handleBack = () => {
    if (stepIndex === 0) return;
    setStepIndex((current) => current - 1);
  };

  const handleSkip = () => {
    finish(doNotShowAgain);
  };

  if (!active || !step) return null;

  const tooltipSize = tooltipRef.current
    ? { width: tooltipRef.current.offsetWidth, height: tooltipRef.current.offsetHeight }
    : { width: 340, height: 260 };

  const tooltipPos = getTooltipPosition(step.placement, targetRect, tooltipSize);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        pointerEvents: 'none',
      }}
      aria-live="polite"
    >
      {targetRect ? (
        <Box
          sx={{
            position: 'fixed',
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            borderRadius: 2,
            border: '2px solid #95F205',
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.78)',
            pointerEvents: 'none',
            transition: 'top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease',
          }}
        />
      ) : (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0, 0, 0, 0.78)',
            pointerEvents: 'none',
          }}
        />
      )}

      <Box
        ref={tooltipRef}
        sx={{
          position: 'fixed',
          top: tooltipPos.top,
          left: tooltipPos.left,
          width: { xs: 'min(92vw, 360px)', sm: 360 },
          pointerEvents: 'auto',
          borderRadius: 3,
          bgcolor: '#1a1d28',
          backgroundImage: `
            radial-gradient(ellipse 80% 50% at 50% -10%, rgba(149, 242, 5, 0.12), transparent 55%),
            linear-gradient(165deg, #2a3348 0%, #1a1d28 48%, #151821 100%)
          `,
          border: '1px solid rgba(149, 242, 5, 0.25)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.55)',
          p: 2.5,
        }}
        role="dialog"
        aria-labelledby="onboarding-walkthrough-title"
      >
        <Typography
          id="onboarding-walkthrough-title"
          sx={{ color: '#fff', fontWeight: 800, fontSize: 18, mb: 1 }}
        >
          {step.title}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 14, lineHeight: 1.55, mb: 2 }}>
          {waitingForTarget ? 'Opening Add coin…' : step.body}
        </Typography>

        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, mb: 1.5 }}>
          Step {stepIndex + 1} of {steps.length}
        </Typography>

        <FormControlLabel
          control={(
            <Checkbox
              checked={doNotShowAgain}
              onChange={(event) => setDoNotShowAgain(event.target.checked)}
              sx={{
                color: 'rgba(255,255,255,0.5)',
                '&.Mui-checked': { color: '#95F205' },
              }}
            />
          )}
          label="Do not show this again"
          sx={{
            mb: 2,
            '& .MuiFormControlLabel-label': {
              color: 'rgba(255,255,255,0.75)',
              fontSize: 13,
            },
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            onClick={handleSkip}
            sx={{ color: 'rgba(255,255,255,0.55)', textTransform: 'none' }}
          >
            Skip tour
          </Button>
          {stepIndex > 0 && (
            <Button
              onClick={handleBack}
              variant="outlined"
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.35)', textTransform: 'none' }}
            >
              Back
            </Button>
          )}
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={waitingForTarget}
            sx={{
              bgcolor: '#95F205',
              color: '#111',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { bgcolor: '#7ed100' },
            }}
          >
            {isLastStep ? 'Finish' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
